import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import {
  getSessionTimeout,
  setSessionTimeout,
  UserKeys,
} from '../../../api/users-api';
import { ModuleList } from '../../../shared/analytics/module-list';

import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SharedTabHeader from '../../../shared/shared-tab-header';
export default function SetSessionTimeout() {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();

  const { administrationRoles, changeLogRoles } = useUserSettings();

  const { data: sessionTimeout, isLoading } = useQuery({
    queryKey: [UserKeys.SessionTimeout],
    queryFn: () => getSessionTimeout(),
  });

  const [timeoutValue, setTimeoutValue] = useState<string>('15');

  useEffect(() => {
    if (!isLoading && sessionTimeout) {
      setTimeoutValue(sessionTimeout.toString());
    }
  }, [isLoading, sessionTimeout]);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      setSessionTimeout(parseInt(timeoutValue), changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Session timeout'),
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SessionTimeout],
      });
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('session timeout'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.update_timeout, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
        timeoutValue,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === '') {
      setTimeoutValue('');
      return;
    }

    const numberValue = Number(value);
    if (!isNaN(numberValue)) {
      setTimeoutValue(value);
    }
  };

  const handleSubmit = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateSessionTimeout.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (parseInt(timeoutValue) >= 5) {
        mutate();
      }
    }
  };

  const descriptionText =
    'Specify the maximum amount of time in minutes that an active gPanel session can be idle (without user input) before automatically being logged out. This sets the timeout for your entire domain. By default, the session timeout is set to 15 minutes.';

  if (isLoading) {
    return (
      <Stack gap={2}>
        <SharedTabHeader title="Session Timeout" />
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {descriptionText}
          </Typography>

          <Stack direction="row" gap={1} mt={2} height={60}>
            <Skeleton
              sx={{
                minWidth: 250,
                height: 40,
              }}
              variant="rounded"
            />
          </Stack>
        </Box>
      </Stack>
    );
  }

  const hasUpdatePermission = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.UpdateSessionTimeout.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Session Timeout" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {descriptionText}
        </Typography>
        <Tooltip
          arrow
          title={
            !hasUpdatePermission
              ? 'You need the "Can update Session Timeout" permission to make changes'
              : ''
          }
        >
          <Stack
            flexDirection="row"
            gap={1}
            mt={2}
            height={60}
            alignItems="baseline"
          >
            <TextField
              label="Idle Timeout (minutes)"
              sx={{ minWidth: 250 }}
              value={timeoutValue}
              disabled={!hasUpdatePermission}
              onChange={handleChange}
              inputProps={{ min: 5, type: 'number' }}
              error={parseInt(timeoutValue) < 5 || timeoutValue === ''}
              helperText={
                parseInt(timeoutValue) < 5 || timeoutValue === ''
                  ? 'Timeout must be at least 5 minutes'
                  : ''
              }
            />

            <Button
              variant="contained"
              disabled={
                parseInt(timeoutValue) < 5 ||
                timeoutValue === '' ||
                !hasUpdatePermission
              }
              loading={isPending}
              type="submit"
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Stack>
        </Tooltip>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      </Box>
    </Stack>
  );
}
